import { useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, CheckCircle2, Clock, Layers, Wind } from "lucide-react";
import { difficultyClass } from "@/lib/api";

const statusLabel = { not_started: "Not started", in_progress: "In progress", completed: "Completed" };
const statusClass = {
  not_started: "text-slate-500",
  in_progress: "text-indigo-700",
  completed: "text-emerald-700",
};

export const TutorialCard = ({ tutorial, progress, onBookmark, index = 0 }) => {
  const [imgOk, setImgOk] = useState(Boolean(tutorial.cover_image));
  const status = progress?.status || "not_started";
  const total = tutorial.step_count || 0;
  const done = progress?.completed_steps?.length || 0;
  const pct = status === "completed" ? 100 : total ? Math.round((done / total) * 100) : 0;

  return (
    <article
      className="group relative flex flex-col rounded-3xl bg-white border border-slate-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-indigo-300 rise"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`tutorial-card-${tutorial.id}`}
    >
      <Link to={`/tutorials/${tutorial.id}`} className="block relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-indigo-100 via-sky-50 to-emerald-100">
        {imgOk && (
          <img src={tutorial.cover_image} alt="" onError={() => setImgOk(false)} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        )}
        {!imgOk && <Wind className="absolute inset-0 m-auto w-14 h-14 text-indigo-300" />}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full backdrop-blur ${difficultyClass[tutorial.difficulty]}`} data-testid={`difficulty-badge-${tutorial.id}`}>
            {tutorial.difficulty}
          </span>
          {tutorial.has_breathing && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/90 text-indigo-700 border border-indigo-100 flex items-center gap-1">
              <Wind className="w-3 h-3" /> Interactive
            </span>
          )}
        </div>
      </Link>

      <button
        type="button"
        onClick={(e) => { e.preventDefault(); onBookmark?.(tutorial); }}
        aria-label={progress?.is_bookmarked ? "Remove bookmark" : "Bookmark tutorial"}
        className={`absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center shadow-sm transition-colors ${progress?.is_bookmarked ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-white/90 text-slate-600 hover:bg-white hover:text-indigo-600"}`}
        data-testid={`bookmark-button-${tutorial.id}`}
      >
        <Bookmark className={`w-4 h-4 ${progress?.is_bookmarked ? "fill-current" : ""}`} />
      </button>

      <div className="flex flex-col flex-1 p-6 gap-3">
        <p className="eyebrow">{tutorial.category_name}</p>
        <Link to={`/tutorials/${tutorial.id}`} className="font-display text-lg sm:text-xl font-bold text-slate-900 leading-snug hover:text-indigo-700 transition-colors" data-testid={`tutorial-title-${tutorial.id}`}>
          {tutorial.title}
        </Link>
        <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{tutorial.summary}</p>
        <div className="mt-auto pt-3 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{tutorial.estimated_minutes} min</span>
            <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{total} steps</span>
          </div>
          <span className={`flex items-center gap-1 font-semibold ${statusClass[status]}`} data-testid={`status-${tutorial.id}`}>
            {status === "completed" && <CheckCircle2 className="w-3.5 h-3.5" />}
            {statusLabel[status]}
          </span>
        </div>
        {status !== "not_started" && (
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ease-out ${status === "completed" ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </article>
  );
};
